/**
 * React binding for the self-schedule state.
 *
 * All business rules live in `reducers.ts` + `selectors.ts` + the
 * `merge/` package — this file is only the React plumbing:
 *
 *   1. `useReducer` over the pure reducers.
 *   2. Memoised pipeline run when inputs change.
 *   3. Derived views (`activeSlots`, `dayRows`, `weekSelection`).
 *   4. Mutation callbacks wrapping each reducer.
 *   5. `onSlotsChange` emitter — fires exactly when the flat slot
 *      list changes.
 *
 * If a bug ends up here, first ask: "can this be a selector
 * instead?" The answer is usually yes.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { BusyEvent, Interval } from '../../../availability';
import { runSlotPipeline } from '../pipeline';
import { ssLog } from '../debug';
import type { SelfSlot } from '../types';
import {
  applyFindTime as applyFindTimeReducer,
  clearAll as clearAllReducer,
  clickCreate as clickCreateReducer,
  moveSlot as moveSlotReducer,
  prune as pruneReducer,
  reconcileSeed as reconcileSeedReducer,
  removeSlot as removeSlotReducer,
  restretchCustoms as restretchCustomsReducer,
  toggleDay as toggleDayReducer,
  type ApplyFindTimeArgs,
  type ClickCreateArgs,
  type MoveSlotArgs,
  type ReconcileSeedArgs,
  type RemoveSlotArgs,
  type ToggleDayArgs,
  type RestretchCustomsArgs,
} from './reducers';
import {
  buildActiveSlots,
  dayCheckboxState,
  weekSelectionState,
  type DayCheckbox,
} from './selectors';
import { seedFromInitial, type InitialSlotsPayload } from './seedFromInitial';
import type { DayStateMap } from './types';

// ─────────────────────────────────────────────────────────────────
// Action shape — one intent per user gesture.
// ─────────────────────────────────────────────────────────────────

type Action =
  | ({ type: 'clickCreate' } & ClickCreateArgs)
  | ({ type: 'toggleDay' } & ToggleDayArgs)
  | ({ type: 'removeSlot' } & RemoveSlotArgs)
  | ({ type: 'moveSlot' } & MoveSlotArgs)
  | ({ type: 'applyFindTime' } & ApplyFindTimeArgs)
  | { type: 'prune' }
  | { type: 'clearAll' }
  | ({ type: 'restretchCustoms' } & RestretchCustomsArgs)
  | ({ type: 'reconcileSeed' } & ReconcileSeedArgs);

function reducer(state: DayStateMap, action: Action): DayStateMap {
  switch (action.type) {
    case 'clickCreate':
      return clickCreateReducer(state, action);
    case 'toggleDay':
      return toggleDayReducer(state, action);
    case 'removeSlot':
      return removeSlotReducer(state, action);
    case 'moveSlot':
      return moveSlotReducer(state, action);
    case 'applyFindTime':
      return applyFindTimeReducer(state, action);
    case 'prune':
      return pruneReducer(state);
    case 'clearAll':
      return clearAllReducer();
    case 'restretchCustoms':
      return restretchCustomsReducer(state, action);
    case 'reconcileSeed':
      return reconcileSeedReducer(state, action);
  }
}

// ─────────────────────────────────────────────────────────────────
// Public API.
// ─────────────────────────────────────────────────────────────────

export interface UseSelfScheduleSlotsArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  duration: number;
  timezone: string;
  minimumNoticeHours?: number;
  /** Whether any interviewer is picked — gates pipeline execution. */
  hasInterviewer: boolean;
  /** Edit-mode seed — mapped to custom picks. */
  initialSlots?: InitialSlotsPayload[];
  /** Fires whenever the emitted slot list actually changes. */
  onSlotsChange?: (_slots: Array<{ start: string; end: string }>) => void;
}

export interface DayRow {
  checkbox: DayCheckbox;
  hasAvailability: boolean;
  slotCount: number;
}

export interface UseSelfScheduleSlotsResult {
  activeSlots: SelfSlot[];
  dayRows: Record<string, DayRow>;
  weekSelection: DayCheckbox;
  clickCreate: (_args: {
    dateKey: string;
    startIso: string;
    endIso: string;
  }) => void;
  toggleDay: (_dateKey: string, _checked: boolean) => void;
  toggleAll: (_checked: boolean) => void;
  /**
   * Remove a slot. Caller passes `isGenerated` because the reducer
   * needs to know whether to stash the id in `droppedIds` or drop
   * from `customPicks`. The calendar knows the origin from the
   * effective pick's `origin` field.
   */
  removeSlot: (_slotId: string, _isGenerated: boolean) => void;
  /**
   * Drag-move a slot. Custom slots move in place (same id) so
   * FullCalendar doesn't unmount+remount — avoids the visual
   * "revert to original position" flicker on cross-day drags.
   */
  moveSlot: (_args: {
    slotId: string;
    wasGenerated: boolean;
    newDateKey: string;
    newStartIso: string;
    newEndIso: string;
  }) => void;
  /**
   * Apply a Find Time selection — resolved dateKeys from the
   * walker. Clears prior picks by default (legacy parity) and
   * flags each target day for auto-fill.
   */
  applyFindTime: (_args: {
    targetDateKeys: string[];
    clearBeforeApply?: boolean;
  }) => void;
  clearAll: () => void;
  /**
   * `true` if the given dateKey has any pipeline availability. Used
   * by the Find Time walker (form-side) to skip "no-availability"
   * days when picking its N targets.
   */
  hasAvailabilityOn: (_dateKey: string) => boolean;
  /**
   * `true` when the proposed window overlaps any currently-emitted
   * pick on the day. Used by `selectAllow` for live drag feedback.
   * Reducer re-checks via `fullyCoveredBy` so correctness doesn't
   * depend on this firing.
   */
  hasOverlap: (_dateKey: string, _startIso: string, _endIso: string) => boolean;
}

export function useSelfScheduleSlots({
  availability,
  busyEvents,
  duration,
  timezone,
  minimumNoticeHours = 0,
  hasInterviewer,
  initialSlots,
  onSlotsChange,
}: UseSelfScheduleSlotsArgs): UseSelfScheduleSlotsResult {
  // ──────────────────── State ────────────────────
  // Capture `timezone` at mount time so the seed lands on the correct
  // date grid. `useReducer`'s lazy init receives these two closed-over
  // values on first render only — subsequent prop changes don't
  // re-seed (reseeding would clobber user picks).
  const [state, dispatch] = useReducer(
    reducer,
    undefined as InitialSlotsPayload[] | undefined,
    () => seedFromInitial(initialSlots, timezone)
  );

  // ──────────────────── Pipeline (pure, memoised) ────────────────────
  const generatedByDay = useMemo(
    () =>
      hasInterviewer
        ? runSlotPipeline({
            availability,
            busyEvents,
            duration,
            timezone,
            minimumNoticeHours,
          })
        : {},
    [
      availability,
      busyEvents,
      duration,
      timezone,
      minimumNoticeHours,
      hasInterviewer,
    ]
  );

  // Keep a ref of current `generatedByDay` so dispatch wrappers can
  // read it without refreshing their identity every render. Avoids
  // React re-render cascades when the calendar wires these
  // callbacks into FullCalendar.
  const generatedRef = useRef(generatedByDay);
  generatedRef.current = generatedByDay;

  // ──────────────────── Derived views ────────────────────
  const activeSlots = useMemo(
    () => buildActiveSlots(state, generatedByDay),
    [state, generatedByDay]
  );

  const dayRows = useMemo<Record<string, DayRow>>(() => {
    const meta: Record<string, DayRow> = {};
    for (const [dateKey, slots] of Object.entries(generatedByDay)) {
      meta[dateKey] = {
        checkbox: dayCheckboxState({
          dateKey,
          state,
          generated: generatedByDay,
          hasAvailability: slots.length > 0,
          isPast: false,
          hasInterviewer,
        }),
        hasAvailability: slots.length > 0,
        slotCount: slots.length,
      };
    }
    for (const dateKey of Object.keys(state)) {
      if (meta[dateKey]) continue;
      meta[dateKey] = {
        checkbox: dayCheckboxState({
          dateKey,
          state,
          generated: generatedByDay,
          hasAvailability: false,
          isPast: false,
          hasInterviewer,
        }),
        hasAvailability: false,
        slotCount: 0,
      };
    }
    return meta;
  }, [state, generatedByDay, hasInterviewer]);

  const weekSelection = useMemo<DayCheckbox>(() => {
    const hasAvailabilityByDate: Record<string, boolean> = {};
    const futureVisibleDateKeys: string[] = [];
    for (const [dateKey, info] of Object.entries(dayRows)) {
      hasAvailabilityByDate[dateKey] = info.hasAvailability;
      futureVisibleDateKeys.push(dateKey);
    }
    return weekSelectionState({
      state,
      generated: generatedByDay,
      hasInterviewer,
      futureVisibleDateKeys,
      hasAvailabilityByDate,
    });
  }, [state, generatedByDay, hasInterviewer, dayRows]);

  // ──────────────────── Emit ────────────────────
  const emitKey = useMemo(
    () => activeSlots.map((s) => `${s.start}|${s.end}`).join(','),
    [activeSlots]
  );
  useEffect(() => {
    ssLog('emit', { count: activeSlots.length });
    onSlotsChange?.(activeSlots.map(({ start, end }) => ({ start, end })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emitKey]);

  // ──────────────────── Lifecycle side effects ────────────────────
  // Only clear when the user actively removes the last interviewer —
  // i.e. hasInterviewer transitions from `true` to `false`. A mount
  // with `hasInterviewer=false` (common in edit mode, where
  // interviewer IDs hydrate on the next tick after the reducer seeds
  // customs) must NOT wipe the seeded picks.
  const prevHasInterviewerRef = useRef<boolean | null>(null);
  useEffect(() => {
    const prev = prevHasInterviewerRef.current;
    prevHasInterviewerRef.current = hasInterviewer;
    if (prev === true && hasInterviewer === false) {
      dispatch({ type: 'clearAll' });
    }
  }, [hasInterviewer]);

  useEffect(() => {
    dispatch({ type: 'restretchCustoms', durationMinutes: duration, timezone });
  }, [duration, timezone]);

  // One-shot reconciliation after edit-mode seeding. `seedFromInitial`
  // puts backend `time_slots` into `customPicks` with `autoFillRequested=false`
  // because it runs before the pipeline has generated anything. Once
  // the pipeline produces slots for a day, diff customs against
  // generated: if they align, flip the day to auto-fill mode so the
  // per-day checkbox renders "full" instead of "partial".
  const hasReconciledSeedRef = useRef(false);
  useEffect(() => {
    if (hasReconciledSeedRef.current) return;
    if (!hasInterviewer) return;
    if (Object.keys(generatedByDay).length === 0) return;
    const hasOverlap = Object.keys(state).some(
      (k) => (generatedByDay[k]?.length ?? 0) > 0
    );
    if (!hasOverlap) return;
    hasReconciledSeedRef.current = true;
    dispatch({ type: 'reconcileSeed', generatedByDay });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generatedByDay, hasInterviewer]);

  // ──────────────────── Dispatch wrappers ────────────────────
  const clickCreate = useCallback(
    (args: { dateKey: string; startIso: string; endIso: string }) => {
      ssLog('clickCreate', args);
      dispatch({
        type: 'clickCreate',
        ...args,
        timezone,
        generatedForDay: generatedRef.current[args.dateKey] ?? [],
      });
    },
    [timezone]
  );

  const toggleDay = useCallback((dateKey: string, checked: boolean) => {
    ssLog('toggleDay', { dateKey, checked });
    dispatch({ type: 'toggleDay', dateKey, checked });
  }, []);

  const toggleAll = useCallback(
    (checked: boolean) => {
      // Rolling toggle — dispatch `toggleDay` for every available
      // day. One reducer path for both per-day + week checkbox.
      // When `checked=true` this also clears each day's `droppedIds`
      // so removed slots repopulate (the expected "refill" when
      // clicking an indeterminate week checkbox).
      const targets = Object.entries(dayRows)
        .filter(([, meta]) => meta.hasAvailability)
        .map(([dateKey]) => dateKey);
      ssLog('toggleAll', { checked, targets });
      for (const dateKey of targets) {
        dispatch({ type: 'toggleDay', dateKey, checked });
      }
    },
    [dayRows]
  );

  const removeSlot = useCallback((slotId: string, isGenerated: boolean) => {
    ssLog('removeSlot', { slotId, isGenerated });
    dispatch({ type: 'removeSlot', slotId, isGenerated });
  }, []);

  const moveSlot = useCallback(
    (args: {
      slotId: string;
      wasGenerated: boolean;
      newDateKey: string;
      newStartIso: string;
      newEndIso: string;
    }) => {
      ssLog('moveSlot', args);
      dispatch({ type: 'moveSlot', ...args });
    },
    []
  );

  const applyFindTime = useCallback(
    (args: { targetDateKeys: string[]; clearBeforeApply?: boolean }) => {
      ssLog('applyFindTime', args);
      dispatch({ type: 'applyFindTime', ...args });
    },
    []
  );

  const hasAvailabilityOn = useCallback(
    (dateKey: string): boolean =>
      (generatedRef.current[dateKey]?.length ?? 0) > 0,
    []
  );

  const clearAll = useCallback(() => {
    ssLog('clearAll');
    dispatch({ type: 'clearAll' });
  }, []);

  const hasOverlap = useCallback(
    (dateKey: string, startIso: string, endIso: string): boolean => {
      const startMs = Date.parse(startIso);
      const endMs = Date.parse(endIso);
      for (const slot of activeSlots) {
        if (slot.dateKey !== dateKey) continue;
        const bStart = Date.parse(slot.start);
        const bEnd = Date.parse(slot.end);
        if (startMs < bEnd && bStart < endMs) return true;
      }
      return false;
    },
    [activeSlots]
  );

  return {
    activeSlots,
    dayRows,
    weekSelection,
    clickCreate,
    toggleDay,
    toggleAll,
    removeSlot,
    moveSlot,
    applyFindTime,
    clearAll,
    hasOverlap,
    hasAvailabilityOn,
  };
}
